import { LockOutlined, MobileOutlined } from '@ant-design/icons';
import {
  LoginFormPage,
  ProFormCaptcha,
  ProFormText,
  PageLoading,
} from '@ant-design/pro-components';
import { Alert, App } from 'antd';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveLoginErrorMessage, resolveLoginPostPath } from '@/apps/auth/utils/login';
import { useAppContext } from '@/common/hooks/AppStateProvider';
import { authClient } from '@/common/utils/auth-client';
import { useQueryParams } from '@/common/hooks/use-query-params';

const BACKGROUND_IMAGE_URL = '/bg-image.jpg';
const BACKGROUND_VIDEO_URL = '/bg-video.mp4';

const LoginMessage = ({ content }) => <Alert title={content} type="error" showIcon />;

LoginMessage.propTypes = {
  content: PropTypes.node,
};

const Login = () => {
  const [userLoginState, setUserLoginState] = useState({});
  const [redirecting, setRedirecting] = useState(false);
  const hasHandledAuthenticatedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [query] = useQueryParams();
  const redirect = Array.isArray(query.redirect) ? query.redirect.at(-1) : query.redirect;
  const { session } = useAppContext();
  const { user } = session;
  const { message } = App.useApp();

  const redirectByOrganizationAccess = useCallback(async () => {
    if (hasHandledAuthenticatedRef.current) {
      return;
    }
    hasHandledAuthenticatedRef.current = true;
    setRedirecting(true);
    if (user?.isPlatformAdmin) {
      const nextPath = resolveLoginPostPath({
        redirect,
        from: location.state?.from,
        user,
      });
      navigate(nextPath, { replace: true });
      return;
    }
    try {
      const { data, error: organizationError } = await authClient.organization.list();
      if (organizationError) {
        message.error(organizationError.message || '获取组织列表失败');
        navigate('/welcome', { replace: true });
        return;
      }
      const nextPath = resolveLoginPostPath({
        redirect,
        from: location.state?.from,
        organizations: data,
        user,
      });
      navigate(nextPath, { replace: true });
    } catch {
      message.error('获取组织列表失败');
      navigate('/welcome', { replace: true });
    }
  }, [location.state, message, navigate, redirect, user]);

  useEffect(() => {
    if (session.status !== 'authenticated') {
      hasHandledAuthenticatedRef.current = false;
      return;
    }
    redirectByOrganizationAccess();
  }, [redirectByOrganizationAccess, session.status]);

  const handleSubmit = async (values) => {
    try {
      const { error } = await authClient.phoneNumber.verify({
        phoneNumber: values.phone,
        code: values.captcha,
      });
      if (error) {
        const errorMessage = resolveLoginErrorMessage(error);
        setUserLoginState({ status: 'error', message: errorMessage });
        message.error(errorMessage);
        return;
      }

      setRedirecting(true);
      const refreshedUser = await session.refresh();
      if (!refreshedUser) {
        setRedirecting(false);
        setUserLoginState({ status: 'error', message: '登录状态刷新失败，请重试' });
        message.error('登录状态刷新失败，请重试');
        return;
      }
      message.success('登录成功');
    } catch (error) {
      setUserLoginState({ status: 'error', message: '登录发生错误' });
      message.error('登录发生错误');
    }
  };

  const { message: loginMessage, status } = userLoginState;

  if (redirecting) {
    return <PageLoading />;
  }

  return (
    <LoginFormPage
      backgroundImageUrl={BACKGROUND_IMAGE_URL}
      backgroundVideoUrl={BACKGROUND_VIDEO_URL}
      logo="/favicon.png"
      title="CNT 文旅新媒体智能体"
      subTitle=" "
      onFinish={async (values) => {
        await handleSubmit(values);
      }}
    >
      {status === 'error' && <LoginMessage content={loginMessage || '验证码错误'} />}

      <ProFormText
        fieldProps={{
          size: 'large',
          prefix: <MobileOutlined />,
        }}
        name="phone"
        placeholder="请输入手机号！"
        rules={[
          {
            required: true,
            message: '手机号是必填项！',
          },
          {
            pattern: /^1\d{10}$/,
            message: '不合法的手机号！',
          },
        ]}
      />
      <ProFormCaptcha
        fieldProps={{
          size: 'large',
          prefix: <LockOutlined />,
        }}
        captchaProps={{
          size: 'large',
        }}
        phoneName="phone"
        placeholder="请输入验证码！"
        captchaTextRender={(timing, count) => {
          if (timing) {
            return `${count} 秒后重新获取`;
          }
          return '获取验证码';
        }}
        name="captcha"
        rules={[
          {
            required: true,
            message: '验证码是必填项！',
          },
        ]}
        onGetCaptcha={async (phone) => {
          const { error } = await authClient.phoneNumber.sendOtp({
            phoneNumber: phone,
          });
          if (error) {
            message.error(error.message || '发送验证码失败');
            return;
          }
          message.success('验证码已发送，请注意查收');
        }}
      />
    </LoginFormPage>
  );
};

export default Login;
